type Coordinate = [number, number]; // [latitude, longitude]

/**
 * Calculates the distance between two coordinates using the Haversine formula.
 * @param coord1 First coordinate [latitude, longitude].
 * @param coord2 Second coordinate [latitude, longitude].
 * @returns Distance in meters.
 */
function haversineDistance(coord1: Coordinate, coord2: Coordinate): number {
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const [lat1, lon1] = coord1;
  const [lat2, lon2] = coord2;

  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Finds the optimal index to insert a new point into a list of coordinates
 * to minimize the total distance.
 * @param coords List of coordinates.
 * @param newPoint New coordinate to insert [latitude, longitude].
 * @returns Index where the new point should be inserted.
 */
export function findInsertionIndex(
  coords: Coordinate[],
  newPoint: Coordinate,
): number {
  let minIncrease = Infinity;
  let bestIndex = 0;

  for (let i = 0; i < coords.length - 1; i++) {
    const d1 = haversineDistance(coords[i], coords[i + 1]);
    const d2 =
      haversineDistance(coords[i], newPoint) +
      haversineDistance(newPoint, coords[i + 1]);

    const increase = d2 - d1;
    if (increase < minIncrease) {
      minIncrease = increase;
      bestIndex = i + 1;
    }
  }

  return bestIndex;
}
